import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ApiException,
    InputType,
    MessageBar,
    TextField,
} from '@eta/components';
import {
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import './InviteAdministrator.scss';
import { ServiceCreator } from './Service/client';
import {
    LanguageTypes,
    SessionAdminInviteRequestDto,
} from './Service/TaxpayerProxy';

export interface InviteAdministratorProps {
    setFooter: (footer: JSX.Element) => void;
    setSuccessInvite: (email: string) => void;
    sessionID: string;
    defaultLanguage: LanguageTypes;
    rin: string;
    hasDigitalProfile: boolean;
}

export const InviteAdministrator: FC<InviteAdministratorProps> = (
    props: InviteAdministratorProps
) => {
    const { t } = useTranslation(['SignUp']);
    const { setFooter, setSuccessInvite, sessionID, defaultLanguage, rin, hasDigitalProfile } =
        props;

    const [nationalID, setNationalID] = useState<string>();
    const [firstName, setFirstName] = useState<string>();
    const [surName, setSurName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [reEnterEmail, setReEnterEmail] = useState<string>();
    const [canInvite, setCanInvite] = useState<boolean>(false);
    const [inValidNationalID, setInValidNationalID] = useState<string>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorrMsg, setErrorrMsg] = useState<string>();

    const client = ServiceCreator.getTaxpayerManagementPublicClient(true);

    const sendInvitation = async () => {
        try {
            setIsLoading(true);
            setErrorrMsg(undefined);
            if(email !== reEnterEmail){
                setErrorrMsg(t('inValidRenterEmail'));
                return;
            }
            const response = await client.sessionAdminInvite(
                sessionID ?? '',
                new SessionAdminInviteRequestDto({
                    dateActivate: new Date(Date.now()),
                    dateDeactivate: undefined,
                    email,
                    nationalID,
                    namePrimaryLang: undefined,
                    surnamePrimaryLang: undefined,
                    preferredLanguage: defaultLanguage,
                    nameSecondaryLang: firstName,
                    surnameSecondaryLang: surName,
                })
            );
            if (response.invitationID) setSuccessInvite(email ?? '');
        } catch (ex) {
            const exception = ex as ApiException;
            setErrorrMsg(
                exception?.serviceError
                    ? exception?.serviceError.toString()
                    : exception.toString()
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setFooter(
            <PrimaryButton
                className="continue"
                menuIconProps={{ iconName: 'Forward' }}
                disabled={!canInvite || isLoading}
                text={t('SendInvitation')}
                onClick={() => sendInvitation()}
            >
                {isLoading && <Spinner size={SpinnerSize.small} />}
            </PrimaryButton>
        );
    }, [
        canInvite,
        t,
        isLoading,
        email,
        reEnterEmail,
        nationalID,
        surName,
        firstName,
    ]);

    useEffect(() => {
        setCanInvite(!!nationalID &&!!firstName && !!surName && !!email && !!reEnterEmail);
    }, [firstName, surName, email, reEnterEmail, nationalID]);

    return (
        <div className="SignUp_InviteAdministrator">
            {errorrMsg && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                    className="messageBar"
                    onDismiss={() => {
                        setErrorrMsg(undefined);
                    }}
                >
                    {errorrMsg}
                </MessageBar>
            )}
            <div className="SignUp_InviteAdministratorContent">
                <div className="contentAdministrator">
                    <p className="bold">{t(hasDigitalProfile ? 'TaxpayerFound' : 'TaxpayerCreated')}</p>
                    <p>
                        <span>{t('SelectPerson')}</span>
                        <span className="bold">{`${t('Taxpayer', { rin })} `}</span>
                        <span>{t('InSystem')}</span>
                        <span>{t('createAnInvitation1')}</span>
                    </p>
                    <p>{t('createAnInvitation2')}</p>
                </div>

                <br />
                <div className="body-InviteAdministrator">
                    <TextField
                        label={t('NationalID')}
                        maxLength={14}
                        required
                        value={nationalID}
                        onChange={(_, v) => {
                            setInValidNationalID(undefined);
                            setNationalID(v);
                        }}
                        inputType={InputType.Number}
                        errorMessage={inValidNationalID}
                    />
                    <TextField
                        label={t('FirstName')}
                        maxLength={50}
                        required
                        value={firstName}
                        onChange={(_, v) => setFirstName(v)}
                    />
                    <TextField
                        label={t('Surname')}
                        maxLength={50}
                        required
                        value={surName}
                        onChange={(_, v) => setSurName(v)}
                    />
                    <TextField
                        className="UserType"
                        label={t('UserType')}
                        maxLength={50}
                        readOnly
                        value={t('Admin')}
                    />
                </div>
                <div className="body-InviteAdministrator">
                    <TextField
                        label={t('LoginEmail')}
                        maxLength={50}
                        required
                        value={email}
                        onChange={(_, v) => setEmail(v)}
                    />
                    <TextField
                        label={t('ReEnterEmail')}
                        maxLength={50}
                        required
                        value={reEnterEmail}
                        onChange={(_, v) => setReEnterEmail(v)}
                    />
                </div>
            </div>
        </div>
    );
};
